import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaIdCard, FaUserCog, FaStar, FaHistory, FaDollarSign } from 'react-icons/fa'; // # Import FaDollarSign icon
import DashboardHeader from '../../../../components/DashboardHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import { auth, db } from '../../../../auth/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import cacheManager from '../../../../auth/cache_manager';
import './subscriptions.css';

// # Main Subscriptions component with navigation icons
const Subscriptions = () => {
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Navigation options with icons and paths
  const subscriptionOptions = [
    {
      title: 'Subscription Management',
      icon: <FaIdCard className="subs_icon" />,
      description: 'Manage user subscriptions and plans',
      path: '/admin/subscriptions/management'
    },
    {
      title: 'Subscription Controls',
      icon: <FaUserCog className="subs_icon" />,
      description: 'Configure subscription settings and rules',
      path: '/admin/subscriptions/controls'
    },
    {
      title: 'Special Subscription',
      icon: <FaStar className="subs_icon" />,
      description: 'Manage special and promotional subscriptions',
      path: '/admin/subscriptions/special'
    },
    {
      title: 'Subscription Logs',
      icon: <FaHistory className="subs_icon" />,
      description: 'View subscription activity logs and history',
      path: '/admin/subscriptions/logs'
    },
    {
      title: 'Pricing Management',
      icon: <FaDollarSign className="subs_icon" />,
      description: 'Manage subscription pricing and plans',
      path: '/admin/subscriptions/pricing'
    }
  ];

  // # Handle navigation to selected option
  const handleOptionClick = (path) => {
    navigate(path);
  };

  return (
    <div className={`subs_container ${isDarkMode ? 'subs_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <div className="subs_content">
        <h1 className="subs_title">Subscription Management Dashboard</h1>
        <p className="subs_subtitle">Select a category to manage subscriptions</p>
        
        <div className="subs_options_grid">
          {subscriptionOptions.map((option, index) => (
            <div 
              key={index} 
              className="subs_option_card"
              onClick={() => handleOptionClick(option.path)}
            >
              <div className="subs_option_icon">
                {option.icon}
              </div>
              <h2 className="subs_option_title">{option.title}</h2>
              <p className="subs_option_description">{option.description}</p>
            </div>
          ))}
        </div>
      </div>
      
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
};

export default Subscriptions;
